/**
 * `useStream` — the client half of the streaming seam (docs/copilot/PRD.md §4.3).
 * POSTs a contract op and reads its NDJSON frame stream, surfacing frames as they
 * arrive. Lives in the api client layer (alongside `apiCall`/`useApi`) so product
 * code never touches `fetch` directly — the no-`fetch` rail holds.
 */

import { useCallback, useRef, useState } from "react";
import { buildUrl, type Operation } from "./types";

export type StreamStatus = "idle" | "streaming" | "done" | "error";

export interface UseStreamResult<TFrame, TBody> {
  /** Frames received during the current/last turn, in order. */
  frames: TFrame[];
  status: StreamStatus;
  error: string | null;
  /** Open a turn with the given body. Resolves when the stream closes. */
  send: (body: TBody) => Promise<void>;
  /** Clear frames + status (e.g. when switching conversations). */
  reset: () => void;
  /** Abort the in-flight turn (Stop button). Keeps the frames received so far;
   *  the server SIGTERMs the child and persists the partial answer. */
  abort: () => void;
}

export function useStream<TParams, TQuery, TBody, TFrame>(
  op: Operation<TParams, TQuery, TBody, TFrame>,
): UseStreamResult<TFrame, TBody> {
  const [frames, setFrames] = useState<TFrame[]>([]);
  const [status, setStatus] = useState<StreamStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setFrames([]);
    setStatus("idle");
    setError(null);
  }, []);

  // Abort the in-flight turn but KEEP the frames + mark done (Stop button). The
  // server sees request.signal abort and SIGTERMs the child, persisting partials.
  const abort = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setStatus((s) => (s === "streaming" ? "done" : s));
  }, []);

  const send = useCallback(
    async (body: TBody) => {
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      setFrames([]);
      setError(null);
      setStatus("streaming");

      const url = buildUrl(op, undefined as TParams, undefined as TQuery);
      try {
        const res = await fetch(url, {
          method: op.method,
          headers: { "content-type": "application/json" },
          body: JSON.stringify(body),
          signal: ac.signal,
        });
        if (!res.body) {
          setStatus("error");
          setError(`no stream body (${res.status})`);
          return;
        }
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = "";
        let sawError = false;
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          let nl: number;
          while ((nl = buf.indexOf("\n")) >= 0) {
            const line = buf.slice(0, nl).trim();
            buf = buf.slice(nl + 1);
            if (!line) continue;
            let frame: TFrame;
            try {
              frame = JSON.parse(line) as TFrame;
            } catch {
              continue;
            }
            const f = frame as { type?: string; message?: string };
            if (f.type === "error") {
              sawError = true;
              setError(f.message ?? "stream error");
            }
            setFrames((prev) => [...prev, frame]);
          }
        }
        setStatus(sawError ? "error" : "done");
      } catch (e) {
        if ((e as Error)?.name === "AbortError") return;
        setStatus("error");
        setError((e as Error)?.message ?? "stream failed");
      }
    },
    [op],
  );

  return { frames, status, error, send, reset, abort };
}
